import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import Layout from '../../../components/layout';
import SEO from '../../../components/seo';
import { SettingProvider } from '../../../lib/context/settings';
import { AdsProvider } from '../../../lib/context/ads';
import { Col, Row, Collapse } from 'antd';
import { Title } from '../../../components/styles';
import { device } from '../../../lib/theme';
import styled from 'styled-components';

const { Panel } = Collapse;

const HeroContainer = styled(Row)`
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  height: 100px;
  margin: auto;
  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      font-size: 56px;
      line-height: 1.2;
    }

    background-position: center center;
  }
`;

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const ServicePlansFAQs: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="AAA Extended Service Plans | AAA"
              description="Have a question about our AAA Vehicle Extended Service Plans? Find your answers here."
              canonicalUrl="/automotive/autobuying/service-plans-faq"
            />

            <HeroContainer align="middle" justify="center">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title style={{ margin: 'auto' }}>
                  AAA Extended Service Plan FAQ
                </Title>
              </Col>
            </HeroContainer>

            <Row style={{ paddingTop: 48, marginBottom: 48 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <p>
                  Have a question about our AAA Vehicle Extended Service Plans?
                  Find your answers below.
                </p>
                <Collapse defaultActiveKey={['1']} ghost>
                  <Panel
                    header="What is an AAA Vehicle Extended Service Plan?"
                    key="1"
                  >
                    <p>
                      Most new vehicles today come with a 3 to 4-year/36,000 to
                      50,000 mile manufactory warranty provided by the factory,
                      known as a “manufacturer’s warranty.” The AAA Vehicle
                      Extended Service Plan provides well after the manufacture
                      warranty expires. Extended coverage is available up to 7
                      years and 230,000 miles (based on the coverage plan
                      selected). Each plan can be customized to meet the
                      driver’s specific driving habits in increments of 30 days
                      and 1,000 miles.
                    </p>
                  </Panel>
                  <Panel
                    header="What is the “Original Manufacturer’s Warranty”?"
                    key="2"
                  >
                    <p>
                      The “Original Manufacturer’s Warranty” is the initial
                      warranty provided by the manufacturer for a new vehicle.
                      The Original Manufacture Warranty was provided by the
                      manufacture and included in the cost of your new vehicle.
                      You did not pay extra for the Original Manufacturer
                      Warranty.
                    </p>
                  </Panel>
                  <Panel
                    header="Is the AAA Vehicle Extended Service Plan secure?"
                    key="3"
                  >
                    <p>
                      The AAA VESP contracts are underwritten by Wesco Insurance
                      Company, a wholly owned subsidiary of AmTrust Financial
                      Group, an A.M. Best A-rated (excellent) company. For more
                      information go to{' '}
                      <a href="http://www.amtrustgroup.com/">
                        www.amtrustgroup.com
                      </a>{' '}
                      or call 212.220.7120 or fax 212.220.7130.
                    </p>
                  </Panel>
                  <Panel
                    header="Why should I choose a AAA Vehicle Extended Service Plan over other mechanical breakdown plans?"
                    key="4"
                  >
                    <p>
                      An A.M. Best Company “A” Rated insurer underwrites the AAA
                      Vehicle Extended Service Plan. AAA carefully researched
                      the program Underwriter, Administrator and coverages to
                      ensure the highest level of integrity and credibility. AAA
                      analyzed the prices to ensure the plans are priced
                      competitively based on the comparable coverage. AAA
                      Vehicle Extended Service Plans provide wide verity of
                      coverage, simple claims processing, and the deductible
                      will be reduced by 50% when covered repairs are completed
                      at a AAA Approved Auto Repair facility throughout the US
                      and Canada. Extended coverage is available up to 7 years
                      and 230,000 miles (based on the coverage plan selected).
                      Each plan is customized to meet the driver’s specific
                      driving habits in increments of 30-days and 1,000 miles.
                    </p>
                  </Panel>
                  <Panel
                    header="Why buy it now? My vehicle still has the manufacturer’s warranty coverage."
                    key="5"
                  >
                    <p>
                      Purchasing long-term coverage today is less expensive than
                      it will be in the future. Think of a AAA Vehicle Extended
                      Service Plan like health insurance for your vehicle. The
                      older your vehicle is, the more expensive the coverage
                      becomes. As your vehicle ages and accrues mileage, the
                      price for coverage will increase and some coverage will
                      become unavailable.
                    </p>
                  </Panel>
                  <Panel
                    header="My vehicle is out of the manufacturer’s warranty; am I eligible for coverage?"
                    key="6"
                  >
                    <p>
                      Levels of coverage are based on vehicle age and odometer
                      miles at the time coverage is purchased. Coverage
                      eligibility extends up to 20 model years and 150,000
                      miles. Maximum coverage terms extend up to 7 years and
                      230,000 miles (based on the coverage plan selected). Each
                      plan is customized to meet the driver’s specific driving
                      habits in increments of 30-days and 1,000 miles.
                    </p>
                  </Panel>
                  <Panel header="What types of coverage are available?" key="7">
                    <p>
                      The AAA Vehicle Extended Service Plan is available in four
                      coverage levels, ranging from Exclusionary to Named
                      Component. Each plan is customized to your exact driving
                      habits in increments of 30-days and 1,000 miles. Depending
                      on the plan and coverage terms you choose, may cover all
                      major mechanical and electrical components for up to 7
                      years and 230,000 odometer miles. Even better, you will
                      enjoy rental car service, trip interruption benefits,
                      direct payment of claims and a host of other AAA benefits
                      designed to get you back on the road quickly and hassle
                      free!
                    </p>
                  </Panel>
                  <Panel
                    header="Where can I take my vehicle for repairs?"
                    key="8"
                  >
                    <p>
                      You may take your vehicle to any state licensed repair
                      facility in the United States or Canada. However, we
                      recommend you take your vehicle to a AAA Approved Auto
                      Repair (AAR) or a AAA Club Owned Repair (COR) facility.
                      The standard deductible per approved claim is $100. If you
                      return the covered vehicle to an AAR the deductible is
                      reduced to $50 and to $0 if returned to a COR.
                    </p>
                  </Panel>
                  <Panel header="How do I file a claim?" key="9">
                    <p>
                      Take the covered vehicle, or have it towed, to the nearest
                      state licensed repair facility, AAR or COR. Notify the
                      facility that the vehicle is covered by a AAA Vehicle
                      Extended Service Plan. After diagnosing the problem, make
                      sure the repair facility contacts the Administrator at our
                      toll-free number (located at the bottom of each page of
                      the AAA Vehicle Extended Service Plan agreement) to obtain
                      repair authorization before beginning any work. Repair
                      authorization must be obtained prior to initiating repair
                      work. If prior authorization is not obtained, your claim
                      will not be honored. When repairs are complete and the
                      repair facility has notified the Administrator that you
                      have accepted the repairs, the repair facility will
                      receive direct payment, less the deductible, via our
                      corporate credit card, which eliminates unnecessary
                      paperwork for you.
                    </p>
                  </Panel>
                  <Panel
                    header="Does a AAA Vehicle Extended Service Plan cover parts and labor?"
                    key="10"
                  >
                    <p>
                      The AAA Vehicle Extended Service Plan contract covers 100%
                      of all parts and labor associated with the repair of a
                      covered part or component.
                    </p>
                  </Panel>
                  <Panel header="What is my deductible?" key="11">
                    <p>
                      The standard deductible per approved claim is $100. If you
                      return the covered vehicle to a AAA Authorized Auto Repair
                      (AAR) facility the deductible is reduced to $50 and if the
                      covered vehicle is returned to a AAA Club Owned Repair
                      (COR) facility the deductible is reduced to $0.
                    </p>
                  </Panel>
                  <Panel
                    header="How can I be sure my future claims will be paid?"
                    key="12"
                  >
                    <p>
                      The AAA Vehicle Extended Service Plan contracts are
                      underwritten by Wesco Insurance Company, a wholly owned
                      subsidiary of AmTrust Financial Group, an A.M. Best
                      A-rated (excellent) company. For more information go to
                      amtrustgroup.com or call 212.220.7120 or fax 212.220.7130.
                    </p>
                  </Panel>
                  <Panel header="Do you offer a payment plan?" key="13">
                    <p>
                      Yes, AAA Vehicle Extended Service Plan provides a zero
                      interest payment plan. Minimum 10% down payment required
                      with payment terms up to 24 months.
                    </p>
                  </Panel>
                  <Panel
                    header="Is it safe to enroll online using my credit card?"
                    key="14"
                  >
                    <p>
                      We use the strongest encryption available on the Internet
                      and are VeriSign-certified. Areas that require entering
                      credit card information are on a Secure Website and your
                      credit card information is encrypted prior to transmission
                      using 128-bit Secure Sockets Layer (SSL) Technology.
                    </p>
                  </Panel>
                  <Panel
                    header="Can I buy this same type of coverage through a dealership?"
                    key="15"
                  >
                    <p>
                      Similar coverage is available through franchise automotive
                      dealers. However, the exact coverage is not available
                      through any channel other than AAA. The AAA Vehicle
                      Extended Service Plan includes unique coverage options and
                      member benefits not available in any other plan. AAA
                      encourages its members to research alternative options.
                      When purchasing similar coverage through a dealership, you
                      may be required to return the vehicle to the dealer to
                      obtain service. With AAA, you have the freedom to go to
                      any AAA Approved Auto Repair facility or licensed repair
                      shop across the United States and Canada.
                    </p>
                  </Panel>
                  <Panel
                    header="Will service facilities be willing to repair my vehicle under my AAA Vehicle Extended Service Plan?"
                    key="16"
                  >
                    <p>
                      Most reputable repair facilities will honor repair work
                      covered by a AAA Vehicle Extended Service Plan. The plan
                      pays the repair facility retail for parts and labor based
                      on standard industry pricing guides, and the repair
                      facility receives payment via a corporate credit card as
                      soon as the repair is complete. Repair facilities are
                      familiar with mechanical breakdown protection plans and
                      they know they can trust AAA.
                    </p>
                  </Panel>
                  <Panel
                    header="Am I eligible for a refund if I cancel my AAA Vehicle Extended Service Plan?"
                    key="17"
                  >
                    <p>
                      AAA wants to make sure each of its members is completely
                      satisfied with their mechanical breakdown protection plan
                      purchase and offers a Money Back Guarantee with every plan
                      sold. If you are not completely satisfied with your plan
                      within the first 60 days (30 days for pre-owned vehicles)
                      and have not filed a claim, you will receive a 100 percent
                      refund, no questions asked. After 60 days (30 days for
                      pre-owned vehicles), you are entitled to a pro rata refund
                      of the unused portion of your plan based on the plan
                      purchase date. A $50 cancellation fee may apply depending
                      on state regulations.
                    </p>
                  </Panel>
                  <Panel
                    header="What happens to my coverage if I trade-in my vehicle?"
                    key="18"
                  >
                    <p>
                      When you purchase a plan, you have coverage throughout the
                      term of your plan. If you decide to trade-in your vehicle,
                      simply call us and we will calculate a pro-rated refund.
                      The plan is not transferable to a Dealer.
                    </p>
                  </Panel>
                  <Panel
                    header="If I sell my vehicle, is my AAA coverage transferable?"
                    key="19"
                  >
                    <p>
                      Your vehicle may be worth more in market value when
                      potential buyers know it includes AAA Vehicle Extended
                      Service Plan coverage. You may transfer your coverage to a
                      private purchaser. A $50 transfer fee may apply depending
                      on state regulations. If you sell or trade-in your vehicle
                      to an automobile dealer, you can cancel your plan and
                      receive a pro rata refund for the unused portion.
                    </p>
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default ServicePlansFAQs;
